// api/knowledgeBasesClient.js
import axios from 'axios';
import TokenService from '../services/token.service';

/**
 * Cliente HTTP configurado para el RAG Service
 * (Knowledge Bases, documentos, búsqueda semántica).
 *
 * Base URL: `REACT_APP_RAG_API_URL` — en dev apunta al dominio custom
 * (`rag-dev.zenitailab.com`). El wire format es JSON camelCase, igual que
 * el resto de APIs públicas de la plataforma.
 *
 * Auth: Bearer JWT (mismo Cognito user pool que los otros services).
 *
 * Header `x-account-id`: se envía siempre que esté disponible en
 * `localStorage.currentAccountId`, alineado con `databaseClient`,
 * `connectionsClient` y `billingClient`. El backend valida el acceso del
 * caller a la `accountId` del recurso.
 */
const knowledgeBasesClient = axios.create({
  baseURL: process.env.REACT_APP_RAG_API_URL,
  timeout: 60000,
  headers: {
    'Content-Type': 'application/json',
  },
});

knowledgeBasesClient.interceptors.request.use(
  (config) => {
    const token = TokenService.getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    const accountId = localStorage.getItem('currentAccountId');
    if (accountId) {
      config.headers['x-account-id'] = accountId;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

knowledgeBasesClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response) {
      console.error('RAG API Error:', {
        status: error.response.status,
        data: error.response.data,
        url: error.config?.url,
      });
      // Push monthly-quota (402) and burst-limit (429) errors into the
      // global banner store so the user sees a persistent reminder instead
      // of just a transient toast. Mirrors `databaseClient`; the store is
      // consumed by the QuotaBanner mounted above the KB view.
      const status = error.response.status;
      if (status === 402 || status === 429) {
        const data = error.response.data ?? {};
        const detail = data.detail ?? {};
        import('../hooks/useQuotaState').then(({ setQuotaError }) => {
          setQuotaError({
            status,
            code: detail.code ?? data.error ?? data.code ?? null,
            message: detail.message ?? data.message ?? 'Plan limit reached',
            raw: data,
          });
        });
      }
    }
    return Promise.reject(error);
  }
);

/**
 * Normaliza errores del rag_service a `{status, message, code, data}`.
 *
 * El backend usa el contrato canónico de error de la plataforma:
 *   `{ detail: { code, message, request_id, ...extras } }`
 * pero también puede devolver `{ error, message }` o cuerpos opacos en 5xx.
 * Esta función deja un shape uniforme para los callers.
 */
export const handleKnowledgeBaseError = (error) => {
  if (error.response) {
    const data = error.response.data ?? {};
    const detail = data.detail ?? {};
    return {
      status: error.response.status,
      code: detail.code ?? data.error ?? data.code ?? null,
      message: detail.message ?? data.message ?? 'RAG service error',
      data,
    };
  }
  if (error.request) {
    return {
      status: 0,
      code: 'NETWORK',
      message: 'Could not reach the RAG service',
      data: null,
    };
  }
  return {
    status: 0,
    code: 'UNKNOWN',
    message: error.message ?? 'Unknown error',
    data: null,
  };
};

export default knowledgeBasesClient;
